import { useEffect, useMemo, useState } from "react";
import { Copy, Download, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { useTakeoff } from "./store";
import { FIXED_LABELS, computeEstimate, defaultEstimateDescription, money, fmtQty } from "./pricing";
import { CARD, INPUT, INPUT_SM, LABEL, MONO, BTN_ORANGE, BTN_GHOST } from "./theme";
import { Overlay, DialogTitle, ConfirmDialog } from "./dialogs";
import QuoteDialog, { downloadQuoteCsv, printQuote } from "./QuoteDialog";
import ExtraEditor, { ExtrasList, newExtra } from "./ExtrasPanel";
import { uid } from "./geometry";

const SECTIONS = ["Pipe Insulation", "Duct Insulation", "Rigid Supports"];
const NONE_LABEL = "__none__";

function ManualItemDialog({ open, onClose, labels, onSave, item }) {
  const [f, setF] = useState({ section: "Pipe Insulation", description: "", label: "", quantity: 1, unit: "ea", rate: 0 });
  useEffect(() => {
    if (open) setF(item ? { ...item } : { section: "Pipe Insulation", description: "", label: "", quantity: 1, unit: "ea", rate: 0 });
  }, [open, item]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{item ? "Edit manual item" : "Add manual item"}</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Section</label>
          <select value={f.section} onChange={(e) => set("section", e.target.value)} className={INPUT} data-testid="manual-item-section">
            {SECTIONS.map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <div><label className={LABEL}>Description</label>
          <input value={f.description} onChange={(e) => set("description", e.target.value)} className={INPUT} data-testid="manual-item-description" /></div>
        <div><label className={LABEL}>Building / location label (optional)</label>
          <select value={f.label} onChange={(e) => set("label", e.target.value)} className={INPUT} data-testid="manual-item-label">
            <option value="">None</option>
            {labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select></div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className={LABEL}>Qty</label><input type="number" min="0" step="0.01" value={f.quantity} onChange={(e) => set("quantity", Number(e.target.value) || 0)} className={INPUT} data-testid="manual-item-qty" /></div>
          <div><label className={LABEL}>Unit</label><input value={f.unit} onChange={(e) => set("unit", e.target.value)} className={INPUT} data-testid="manual-item-unit" /></div>
          <div><label className={LABEL}>Unit price</label><input type="number" min="0" step="0.01" value={f.rate} onChange={(e) => set("rate", Number(e.target.value) || 0)} className={INPUT} data-testid="manual-item-rate" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} disabled={!f.description.trim()} onClick={() => { onSave(f); onClose(); }} data-testid="manual-item-save">{item ? "Save" : "Add item"}</button>
        </div>
      </div>
    </Overlay>
  );
}

const EditableMoney = ({ value, onCommit, testid }) => (
  <input
    key={value}
    type="number" min="0" step="0.01" defaultValue={Number(value || 0).toFixed(2)}
    onBlur={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v !== Number(value)) onCommit(v); }}
    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    className={`${INPUT_SM} ${MONO} h-7 w-24 text-right`}
    aria-label="Unit price"
    data-testid={testid}
  />
);

const EditableText = ({ value, onCommit, testid, readOnly }) => readOnly
  ? <span className="text-slate-800">{value}</span>
  : (
    <input
      key={value}
      defaultValue={value}
      onBlur={(e) => { if (e.target.value.trim() && e.target.value !== value) onCommit(e.target.value.trim()); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className="w-full min-w-[180px] rounded border border-transparent bg-transparent px-1 py-0.5 text-sm text-slate-800 hover:border-slate-200 focus:border-[#f97316] focus:outline-none"
      aria-label="Description"
      data-testid={testid}
    />
  );

/** Estimate view — live-priced from the saved takeoff document. */
export default function EstimateView({ onScopeChange }) {
  const { doc, mutate, activeProject, updateJobItem, jobItems } = useTakeoff();
  const [labelScope, setLabelScope] = useState("");
  const [tagScope, setTagScope] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [editManual, setEditManual] = useState(null);
  const [delManual, setDelManual] = useState(null);
  const [quoteNew, setQuoteNew] = useState(false);
  const [quoteEdit, setQuoteEdit] = useState(null);
  const [quoteDel, setQuoteDel] = useState(null);
  const [extraEdit, setExtraEdit] = useState(null); // extra object (or newExtra()) being edited
  const [extraDel, setExtraDel] = useState(null);

  const overrides = useMemo(() => ({
    support: doc?.supportPriceOverrides || {},
    pvc: doc?.pvcPriceOverrides || {},
    duct: doc?.ductPriceOverrides || {},
  }), [doc]);

  const items = doc?.items || [];
  const manual = doc?.manualEstimateItems || [];

  const customLabels = useMemo(
    () => [...new Set(items.flatMap((i) => (i.labels || []).filter((l) => !FIXED_LABELS.includes(l))))].sort((a, b) => a.localeCompare(b)),
    [items]
  );
  const allLabels = useMemo(
    () => [...new Set(items.flatMap((i) => i.labels || []))].sort((a, b) => a.localeCompare(b)),
    [items]
  );
  const allTags = useMemo(() => {
    const m = new Map();
    for (const i of items) m.set(i.tagId || `${i.name}|${i.unit}|${i.rate}`, i.name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [items]);

  const scopeItems = useMemo(() => {
    let its = items, man = manual;
    if (labelScope === NONE_LABEL) {
      its = its.filter((i) => !(i.labels || []).some((l) => !FIXED_LABELS.includes(l)));
      man = man.filter((m) => !m.label);
    } else if (labelScope) {
      its = its.filter((i) => (i.labels || []).includes(labelScope));
      man = man.filter((m) => m.label === labelScope);
    }
    if (tagScope) its = its.filter((i) => (i.tagId || `${i.name}|${i.unit}|${i.rate}`) === tagScope);
    return { its, man };
  }, [items, manual, labelScope, tagScope]);

  const complete = useMemo(() => computeEstimate(items, manual, overrides), [items, manual, overrides]);
  const scoped = useMemo(
    () => (labelScope || tagScope) ? computeEstimate(scopeItems.its, scopeItems.man, overrides) : complete,
    [labelScope, tagScope, scopeItems, complete, overrides]
  );
  const scopedHere = !!(labelScope || tagScope);
  const scopeName = labelScope === NONE_LABEL ? "No building / room label" : labelScope || (tagScope ? `Item: ${allTags.find(([k]) => k === tagScope)?.[1] || tagScope}` : "Complete job");

  // ---- extra quotes (priced label snapshots — Oct-9 list #3) ----
  // Saved quotes never feed back into the job total: they are snapshots of a
  // label's priced scope + a stored % adjustment, kept in doc.extraQuotes.
  const quotes = doc?.extraQuotes || [];
  const saveQuote = (q) => mutate((d) => {
    d.extraQuotes = d.extraQuotes || [];
    const i = d.extraQuotes.findIndex((x) => x.id === q.id);
    if (i >= 0) d.extraQuotes[i] = q; else d.extraQuotes.push(q);
  });
  const computeLiveForLabel = (lbl) => computeEstimate(
    items.filter((i) => (i.labels || []).includes(lbl)),
    manual.filter((m) => m.label === lbl),
    overrides
  );

  // ---- independent extras (Oct-13 list #2) ----
  // Free-standing priced scopes (CSI-070…) stored in doc.extras. They never
  // feed the job total and never touch source takeoffs — items pulled from
  // the estimate are plain copies.
  const extras = doc?.extras || [];
  const saveExtra = (x) => mutate((d) => {
    d.extras = d.extras || [];
    const i = d.extras.findIndex((e) => e.id === x.id);
    if (i >= 0) d.extras[i] = x; else d.extras.push(x);
  });

  useEffect(() => { onScopeChange?.(labelScope || tagScope ? { ...(labelScope ? { label: labelScope } : {}), ...(tagScope ? { tag: tagScope } : {}) } : {}); }, [labelScope, tagScope]); // eslint-disable-line

  // building/location totals on the COMPLETE job
  const locationCards = useMemo(() => customLabels.map((lbl) => {
    const its = items.filter((i) => (i.labels || []).includes(lbl));
    const man = manual.filter((m) => m.label === lbl);
    const e = computeEstimate(its, man, overrides);
    return { name: lbl, count: its.length, insulation: e.pipeTotal + e.ductTotal, supports: e.supportTotal };
  }), [items, manual, customLabels, overrides]);
  const unassigned = useMemo(() => {
    const its = items.filter((i) => !(i.labels || []).some((l) => !FIXED_LABELS.includes(l)));
    const man = manual.filter((m) => !m.label);
    if (!its.length && !man.length) return null;
    const e = computeEstimate(its, man, overrides);
    return { count: its.length, insulation: e.pipeTotal + e.ductTotal, supports: e.supportTotal };
  }, [items, manual, overrides]);

  // ---- description / notes (stored per project id like the export reads) ----
  const pid = activeProject?.project_id || "";
  const autoDescription = useMemo(() => defaultEstimateDescription(items), [items]);
  const description = (doc?.estimateDescriptions || {})[pid] ?? autoDescription;
  const notes = (doc?.estimatingNotes || {})[pid] || "";

  // ---- price/description commits ----
  const commitRate = (line, v) => {
    if (line.supportKey) {
      mutate((d) => { d.supportPriceOverrides[line.supportKey] = v; });
    } else if (line.pvcKey) {
      mutate((d) => { d.pvcPriceOverrides[line.pvcKey] = v; });
    } else if (line.ductKey) {
      mutate((d) => { d.ductPriceOverrides[line.ductKey] = v; });
    } else if (line.manualId) {
      mutate((d) => { const m = d.manualEstimateItems.find((x) => x.id === line.manualId); if (m) m.rate = v; });
    } else if (line.tagId) {
      updateJobItem(line.tagId, { rate: v });
    } else {
      mutate((d) => { for (const it of d.items) if (it.name === line.itemName && !it.tagId) it.rate = v; });
    }
  };
  const commitDescription = (line, text) => {
    if (line.manualId) {
      mutate((d) => { const m = d.manualEstimateItems.find((x) => x.id === line.manualId); if (m) m.description = text; });
    } else if (line.tagId) {
      updateJobItem(line.tagId, { name: text });
    } else {
      mutate((d) => { for (const it of d.items) if (it.name === line.itemName && !it.tagId) it.name = text; });
    }
  };
  const commitQty = (line, v) => {
    if (line.manualId) mutate((d) => { const m = d.manualEstimateItems.find((x) => x.id === line.manualId); if (m) m.quantity = v; });
  };
  const commitUnit = (line, v) => {
    if (line.manualId) mutate((d) => { const m = d.manualEstimateItems.find((x) => x.id === line.manualId); if (m) m.unit = v; });
  };

  if (!doc) return <div className="flex min-h-[300px] items-center justify-center text-sm text-slate-400">Loading…</div>;

  const cards = [
    { label: "Pipe Insulation", value: money(scoped.pipeTotal), sub: `${scoped.pipeLines.length} line items — equipment included`, testid: "est-pipe-card" },
    { label: "Duct Insulation", value: money(scoped.ductTotal), sub: `${scoped.ductLines.length} line items`, testid: "est-duct-card" },
    { label: "Rigid Pipe Supports", value: money(scoped.supportTotal), sub: `${scoped.supportCount} automatic supports${manual.filter((m) => m.section === "Rigid Supports").length ? ` + ${manual.filter((m) => m.section === "Rigid Supports").length} manual` : ""}`, testid: "est-support-card" },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 p-4 sm:p-6" data-testid="takeoff-estimate-view">
      {/* summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={`${CARD} p-4`} data-testid={c.testid}>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{c.label}</p>
            <p className={`${MONO} mt-1 text-xl font-bold text-slate-900`}>{c.value}</p>
            <p className="mt-0.5 text-[10px] text-slate-400">{c.sub}</p>
          </div>
        ))}
        <div className="rounded-xl bg-[#0b172a] p-4 shadow" data-testid="est-total-card">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50">Complete Total</p>
          <p className={`${MONO} mt-1 text-xl font-bold text-[#fb923c]`} data-testid="est-grand-total">{money(scoped.total)}</p>
          <p className="mt-0.5 text-[10px] text-white/40">{scopedHere ? `Scoped — ${scopeName}` : "Whole job"}</p>
        </div>
      </div>

      {/* scope selectors */}
      <div className={`${CARD} flex flex-wrap items-end gap-3 p-3`} data-testid="est-scope-selectors">
        <div className="min-w-[220px]">
          <label className={LABEL}>Prices by label / building</label>
          <select value={labelScope} onChange={(e) => setLabelScope(e.target.value)} className={INPUT} data-testid="est-label-scope">
            <option value="">All labels</option>
            {allLabels.map((l) => <option key={l} value={l}>{l}</option>)}
            <option value={NONE_LABEL}>No building / room label</option>
          </select>
        </div>
        <div className="min-w-[220px]">
          <label className={LABEL}>Prices by takeoff item</label>
          <select value={tagScope} onChange={(e) => setTagScope(e.target.value)} className={INPUT} data-testid="est-tag-scope">
            <option value="">All takeoff items</option>
            {allTags.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
          </select>
        </div>
        <button onClick={() => setManualOpen(true)} className={`${BTN_ORANGE} ml-auto`} data-testid="est-add-manual"><Plus className="h-3.5 w-3.5" /> Add item</button>
      </div>

      {/* location cards */}
      {(locationCards.length > 0 || unassigned) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="est-location-cards">
          {locationCards.map((c) => (
            <button key={c.name} onClick={() => setLabelScope(c.name)}
              className={`${CARD} p-3 text-left transition-colors hover:border-[#f97316]/60 ${labelScope === c.name ? "border-[#f97316] ring-1 ring-[#f97316]" : ""}`}
              data-testid={`est-location-${c.name.replace(/\s/g, "-")}`}>
              <p className="text-sm font-semibold text-slate-800">{c.name} <span className="ml-1 text-[10px] font-normal text-slate-400">{c.count} takeoffs</span></p>
              <p className={`${MONO} mt-1 text-xs text-slate-600`}>Total without supports <span className="font-bold text-slate-900">{money(c.insulation)}</span></p>
              <p className={`${MONO} text-xs text-slate-600`}>Rigid supports <span className="font-bold text-slate-900">{money(c.supports)}</span></p>
            </button>
          ))}
          {unassigned && (
            <button onClick={() => setLabelScope(NONE_LABEL)}
              className={`${CARD} p-3 text-left transition-colors hover:border-[#f97316]/60 ${labelScope === NONE_LABEL ? "border-[#f97316] ring-1 ring-[#f97316]" : ""}`}
              data-testid="est-location-unassigned">
              <p className="text-sm font-semibold text-slate-800">Not assigned to a building <span className="ml-1 text-[10px] font-normal text-slate-400">{unassigned.count} takeoffs</span></p>
              <p className={`${MONO} mt-1 text-xs text-slate-600`}>Total without supports <span className="font-bold text-slate-900">{money(unassigned.insulation)}</span></p>
              <p className={`${MONO} text-xs text-slate-600`}>Rigid supports <span className="font-bold text-slate-900">{money(unassigned.supports)}</span></p>
            </button>
          )}
          <p className="col-span-full text-[10px] text-slate-400">A takeoff with multiple location labels appears in every matching view, so location totals can overlap. Support counts round up separately per building, so label totals may not sum exactly to the complete-job supports.</p>
        </div>
      )}

      {/* selected-scope bar */}
      {scopedHere && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-[#0b172a] px-4 py-3 text-white" data-testid="est-scope-bar">
          <span className="text-sm font-bold">{scopeName}</span>
          <span className="text-[11px] text-white/50">{scopeItems.its.length} takeoffs · {scopeItems.man.length} manual items</span>
          <span className={`${MONO} text-sm font-bold text-[#fb923c]`}>{money(scoped.total)}</span>
          <span className="text-[11px] text-white/50">complete job {money(complete.total)}</span>
          {labelScope && labelScope !== NONE_LABEL && (
            <button onClick={() => setQuoteNew(true)} className="rounded-lg bg-[#f97316] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-[#ea6a0a]" data-testid="est-create-quote">
              Create Extra / Quote
            </button>
          )}
          <button onClick={() => { setLabelScope(""); setTagScope(""); }} className="ml-auto rounded-lg border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:bg-white/10" data-testid="est-show-complete">Show complete job</button>
        </div>
      )}

      {/* saved extra quotes — snapshots; opening one shows ITS prices, not live takeoffs */}
      {quotes.length > 0 && (
        <div className={CARD} data-testid="est-quotes">
          <p className="border-b border-slate-100 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            Extra quotes — priced snapshots, never added to the job total
          </p>
          <div className="divide-y divide-slate-100">
            {quotes.map((q) => (
              <div key={q.id} className="flex flex-wrap items-center gap-2 px-4 py-2" data-testid={`est-quote-${q.id}`}>
                <button className="min-w-0 flex-1 text-left" onClick={() => setQuoteEdit(q)} data-testid={`est-quote-open-${q.id}`}>
                  <span className="block truncate text-sm font-semibold text-slate-800 hover:text-[#f97316]">{q.name}</span>
                  <span className="block text-[10px] text-slate-400">
                    {q.label} · {new Date(q.updatedAt || q.createdAt).toLocaleDateString()} · subtotal {money(q.subtotal)}
                    {q.adjustment?.direction && q.adjustment.direction !== "none" && q.adjustment.pct ? ` · ${q.adjustment.direction === "deduct" ? "−" : "+"}${q.adjustment.pct}%${q.adjustment.reason ? ` (${q.adjustment.reason})` : ""}` : ""}
                  </span>
                </button>
                <span className={`${MONO} text-sm font-bold text-slate-900`} data-testid={`est-quote-total-${q.id}`}>{money(q.total)}</span>
                <div className="flex items-center gap-0.5">
                  <button aria-label={`Edit quote ${q.name}`} title="Edit" onClick={() => setQuoteEdit(q)} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`est-quote-edit-${q.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button aria-label={`Duplicate quote ${q.name}`} title="Duplicate" onClick={() => saveQuote({ ...q, id: uid(), name: `${q.name} (copy)`, createdAt: Date.now(), updatedAt: Date.now() })} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`est-quote-dup-${q.id}`}><Copy className="h-3.5 w-3.5" /></button>
                  <button aria-label={`Export quote ${q.name} as CSV`} title="Excel / CSV" onClick={() => downloadQuoteCsv(q)} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`est-quote-csv-${q.id}`}><Download className="h-3.5 w-3.5" /></button>
                  <button aria-label={`Print quote ${q.name}`} title="Print" onClick={() => printQuote(q)} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`est-quote-print-${q.id}`}><Printer className="h-3.5 w-3.5" /></button>
                  <button aria-label={`Delete quote ${q.name}`} title="Delete" onClick={() => setQuoteDel(q)} className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500" data-testid={`est-quote-del-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* independent extras — free-standing priced scopes (CSI-070…) */}
      <ExtrasList extras={extras} jobItems={jobItems} measuredItems={items} onOpen={setExtraEdit} onCreate={() => setExtraEdit(newExtra())} onDelete={setExtraDel} />

      {/* detailed table */}
      <div className={CARD} data-testid="est-detail-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                <th className="px-4 py-2.5 w-6"></th>
                <th className="px-2 py-2.5">Description</th>
                <th className="px-2 py-2.5">Labels</th>
                <th className="px-2 py-2.5 text-right">Qty</th>
                <th className="px-2 py-2.5">Unit</th>
                <th className="px-2 py-2.5 text-right">Unit price</th>
                <th className="px-4 py-2.5 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map((section) => {
                const rows = scoped.lines.filter((l) => l.section === section);
                if (!rows.length) return null;
                const sub = rows.reduce((s, l) => s + l.quantity * l.rate, 0);
                return (
                  <SectionRows key={section} section={section} rows={rows} sub={sub}
                    onRate={commitRate} onDesc={commitDescription} onQty={commitQty} onUnit={commitUnit}
                    onEditManual={(l) => setEditManual(manual.find((m) => m.id === l.manualId))}
                    onDelManual={(l) => setDelManual(l)} />
                );
              })}
              {!scoped.lines.length && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">Nothing measured yet — trace pipe, duct and counts in Plans &amp; Takeoffs and they price themselves here.</td></tr>
              )}
            </tbody>
            {scoped.lines.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-800 bg-slate-50">
                  <td colSpan={6} className="px-4 py-3 text-right text-xs font-bold uppercase tracking-widest text-slate-500">
                    {scopedHere ? `Total — ${scopeName}` : "Grand total"}
                  </td>
                  <td className={`px-4 py-3 text-right ${MONO} text-base font-bold text-slate-900`} data-testid="est-table-total">{money(scoped.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* scope description + notes */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className={`${CARD} p-4`} data-testid="est-scope-description">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Scope description</p>
            <button onClick={() => mutate((d) => { delete d.estimateDescriptions[pid]; })} className="text-[10px] font-semibold uppercase tracking-wider text-[#f97316] hover:underline" data-testid="est-use-automatic">Use automatic</button>
          </div>
          <textarea value={description} rows={8}
            onChange={(e) => mutate((d) => { d.estimateDescriptions[pid] = e.target.value; })}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 focus:border-[#f97316] focus:outline-none"
            data-testid="est-description-text" />
        </div>
        <div className={`${CARD} p-4`} data-testid="est-notes">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Estimating notes</p>
          <textarea value={notes} rows={8} placeholder="Assumptions, drawing notes, exclusions…"
            onChange={(e) => mutate((d) => { d.estimatingNotes[pid] = e.target.value; })}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 focus:border-[#f97316] focus:outline-none"
            data-testid="est-notes-text" />
        </div>
      </div>

      <ManualItemDialog open={manualOpen} onClose={() => setManualOpen(false)} labels={customLabels}
        onSave={(f) => mutate((d) => { d.manualEstimateItems.push({ ...f, id: uid(), label: f.label || null }); })} />
      <ManualItemDialog open={!!editManual} onClose={() => setEditManual(null)} labels={customLabels} item={editManual}
        onSave={(f) => mutate((d) => { const i = d.manualEstimateItems.findIndex((x) => x.id === editManual.id); if (i >= 0) d.manualEstimateItems[i] = { ...f, id: editManual.id, label: f.label || null }; })} />
      <ConfirmDialog open={!!delManual} onClose={() => setDelManual(null)}
        title={`Delete "${delManual?.description}"?`} body="The manual estimate row is removed." confirmLabel="Delete"
        onConfirm={() => mutate((d) => { d.manualEstimateItems = d.manualEstimateItems.filter((x) => x.id !== delManual.manualId); })} />
      <QuoteDialog open={quoteNew} onClose={() => setQuoteNew(false)} label={labelScope} freshEst={scoped} onSave={saveQuote} />
      <QuoteDialog open={!!quoteEdit} onClose={() => setQuoteEdit(null)} quote={quoteEdit}
        computeLive={() => computeLiveForLabel(quoteEdit.label)} onSave={saveQuote} />
      <ConfirmDialog open={!!quoteDel} onClose={() => setQuoteDel(null)}
        title={`Delete quote "${quoteDel?.name}"?`}
        body="Only the saved quote is removed — the takeoffs it was priced from stay exactly as they are."
        confirmLabel="Delete"
        onConfirm={() => mutate((d) => { d.extraQuotes = (d.extraQuotes || []).filter((x) => x.id !== quoteDel.id); })} />
      <ExtraEditor open={!!extraEdit} onClose={() => setExtraEdit(null)} extra={extraEdit}
        estimateLines={complete.lines} onSave={saveExtra} />
      <ConfirmDialog open={!!extraDel} onClose={() => setExtraDel(null)}
        title={`Delete extra "${extraDel?.name}"?`}
        body="Only the extra is removed — the estimate and every other extra stay exactly as they are."
        confirmLabel="Delete extra"
        onConfirm={() => mutate((d) => { d.extras = (d.extras || []).filter((x) => x.id !== extraDel.id); })} />
    </div>
  );
}

function SectionRows({ section, rows, sub, onRate, onDesc, onQty, onUnit, onEditManual, onDelManual }) {
  return (
    <>
      <tr className="bg-slate-100/70">
        <td colSpan={6} className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{section}</td>
        <td className={`px-4 py-1.5 text-right ${""}font-mono text-xs font-bold text-slate-700`}>{money(sub)}</td>
      </tr>
      {rows.map((l, idx) => (
        <tr key={`${section}-${idx}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
          <td className="px-4 py-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: l.color || "#cbd5e1" }} /></td>
          <td className="px-2 py-1.5">
            <span className="flex items-center gap-1.5">
              <EditableText value={l.description} readOnly={l.source === "Automatic"}
                onCommit={(t) => onDesc(l, t)} testid={`est-desc-${section.replace(/\s/g, "")}-${idx}`} />
              {l.source === "Manual" && <span className="rounded bg-slate-200 px-1 py-px text-[8px] font-bold uppercase text-slate-500">Manual</span>}
              {l.source === "Automatic" && <span className="rounded bg-blue-100 px-1 py-px text-[8px] font-bold uppercase text-blue-600">Auto every 7 ft</span>}
            </span>
          </td>
          <td className="px-2 py-1.5">
            {(l.labels || []).map((lbl) => (
              <span key={lbl} className="mr-1 rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-semibold text-slate-500">{lbl}</span>
            ))}
          </td>
          <td className="px-2 py-1.5 text-right">
            {l.source === "Manual" ? (
              <input key={l.quantity} type="number" min="0" step="0.01" defaultValue={l.quantity}
                onBlur={(e) => onQty(l, Number(e.target.value) || 0)}
                className="h-7 w-20 rounded-md border border-slate-300 px-1.5 text-right font-mono text-xs focus:border-[#f97316] focus:outline-none"
                aria-label="Quantity" data-testid={`est-qty-${section.replace(/\s/g, "")}-${idx}`} />
            ) : (
              <span className="font-mono text-xs text-slate-700">{fmtQty(l.quantity)}</span>
            )}
          </td>
          <td className="px-2 py-1.5">
            {l.source === "Manual" ? (
              <input key={l.unit} defaultValue={l.unit} onBlur={(e) => onUnit(l, e.target.value || "ea")}
                className="h-7 w-16 rounded-md border border-slate-300 px-1.5 font-mono text-xs focus:border-[#f97316] focus:outline-none"
                aria-label="Unit" data-testid={`est-unit-${section.replace(/\s/g, "")}-${idx}`} />
            ) : (
              <span className="font-mono text-xs text-slate-500">{l.unit}</span>
            )}
          </td>
          <td className="px-2 py-1.5 text-right">
            <span className="inline-flex items-center gap-1">
              <EditableMoney value={l.rate} onCommit={(v) => onRate(l, v)} testid={`est-rate-${section.replace(/\s/g, "")}-${idx}`} />
              {l.source === "Manual" && (
                <>
                  <button aria-label="Edit manual item" className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" onClick={() => onEditManual(l)} data-testid={`est-edit-manual-${idx}`}>✎</button>
                  <button aria-label="Delete manual item" className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500" onClick={() => onDelManual(l)} data-testid={`est-del-manual-${idx}`}><Trash2 className="h-3 w-3" /></button>
                </>
              )}
            </span>
          </td>
          <td className="px-4 py-1.5 text-right font-mono text-xs font-bold text-slate-900">{money(l.quantity * l.rate)}</td>
        </tr>
      ))}
    </>
  );
}
